import React, { useRef, useState } from 'react';
import { AvTag } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Button, Popover, useTheme } from '@mui/material';
import { useDynamicVisibleItemsLimit } from './hooks/useDynamicVisibleItemsLimit';

const wrapperStyle = {
  minHeight: 32,
  display: 'contents',
  '> span:first-of-type': {
    lineHeight: '32px',
    alignSelf: 'start',
    flexShrink: 0,
  },
  '> *': {
    ...flex.itemsCenter,
  },
  a: {
    textDecoration: 'none',
  },
};

interface IAvTagsList {
  tags: string[];
  numOfTags?: number;
  tagBg?: string;
}

export const AvTagsList: React.FC<IAvTagsList> = ({ tags = [], numOfTags, tagBg }) => {
  const theme = useTheme();
  const sx = {
    ...flex.row,
    ...ellipsis,
    WebkitLineClamp: '1',
    columnGap: 1,
    rowGap: '3px',
    '.extra-items': {
      height: 20,
      padding: '1px 7px',
      backgroundColor: tagBg?.startsWith('#') ? tagBg : theme.palette.colors.neutrals[350],
      border: 0,
      borderRadius: '8px',
      fontSize: 13,
      fontWeight: 500,
      '&:hover': {
        backgroundColor: tagBg?.startsWith('#') ? tagBg : theme.palette.colors.neutrals[350],
        border: 0,
        borderRadius: '8px',
      },
    },
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const rootRef = useRef<HTMLElement>(null);

  const limit = useDynamicVisibleItemsLimit(rootRef, {
    fixedLimit: numOfTags,
    items: tags,
    overlapBetweenItems: 8,
    reservedSpaceForLastElement: 100,
    additionalPaddingPerItem: 12,
    parentElement: rootRef.current?.parentElement?.parentElement?.parentElement || null,
  });
  const handleClick = () => setAnchorEl(buttonRef.current);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const generateTag = (label, bg) => (
    <Box key={label} sx={{ paddingTop: '5px' }}>
      <AvTag oneLine text={label} bg={bg} />
    </Box>
  );

  const hasOverflow = tags.length > limit;
  const shownItems = hasOverflow ? tags.slice(0, limit) : tags;
  const overflowItems = tags.slice(limit);

  const paperStyle = {
    ...flex.col,
    gap: 2,
    pl: 1,
    py: 2,
    pr: 4,
    backgroundColor: theme.palette.white.main,
    minWidth: 'fit-content',
  };

  return (
    <Box sx={wrapperStyle}>
      <Box sx={sx} ref={rootRef}>
        {shownItems.map(label => generateTag(label, tagBg))}
        {hasOverflow && (
          <>
            <Button ref={buttonRef} variant="outlined" className="extra-items" onMouseEnter={handleClick} onMouseLeave={handleClose}>
              +{tags.length - limit}
            </Button>
            <Popover
              onClick={e => e.stopPropagation()}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              PaperProps={{ sx: paperStyle }}
              sx={{ pointerEvents: 'none', ml: 2 }}
              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'left' }}>
              {overflowItems.map(label => generateTag(label, tagBg))}
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};
